import { useIsMobile } from '@/hooks/useIsMobile';
import { useWindowSize } from '@react-hook/window-size';
import { createContext, useEffect, useState } from 'react';

export enum RwdFitModeEnum {
    FIT_WIDTH,
    FIT_HEIGHT,
}

export const ScreenContext = createContext({} as ScreenState);
const { Provider } = ScreenContext;

type ScreenProviderOwnProps = {
    children: JSX.Element;
};

type ScreenState = {
    isLandscape: boolean;
    isMobile: boolean;
    scaleFactor: number;
    containerWidth: number;
    containerHeight: number;
    fitMode: number;
    isApple: boolean;
};

const PORTRAIT_BASE_SCREEN = { WIDTH: 540, HEIGHT: 960 };
const LANDSCAPE_BASE_SCREEN = { WIDTH: 1920, HEIGHT: 1080 };

// const FIT_HEIGHT_PORTRAIT_MAX_ASPECT_RATIO = 3 / 4;
const FIT_HEIGHT_LANDSCAPE_MAX_ASPECT_RATIO = 21 / 9;

const FIT_WIDTH_PORTRAIT_MAX_ASPECT_RATIO = 9 / 21;
const FIT_WIDTH_LANDSCAPE_MAX_ASPECT_RATIO = 4 / 3;

export const ScreenProvider = (props: ScreenProviderOwnProps) => {
    const { children } = props;

    const [isLandscape, setIsLandscape] = useState(false);
    const [scaleFactor, setScaleFactor] = useState<number>(1);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [containerHeight, setContainerHeight] = useState<number>(0);
    const [fitMode, setFitMode] = useState<number>(RwdFitModeEnum.FIT_WIDTH);

    const { isMobile, isApple } = useIsMobile();

    const [width, height] = useWindowSize();

    useEffect(() => {
        const baseScreenSize = isLandscape
            ? LANDSCAPE_BASE_SCREEN
            : PORTRAIT_BASE_SCREEN;

        const newScaleFactor =
            Math.round(
                Math.min(
                    width / baseScreenSize.WIDTH,
                    height / baseScreenSize.HEIGHT
                ) * 100
            ) / 100;
        setScaleFactor(newScaleFactor);

        const scaleFactorByWidth =
            Math.round((width / baseScreenSize.WIDTH) * 100) / 100;
        const isFitWidth = scaleFactorByWidth === newScaleFactor;

        let maxWidth: number = 0;
        let maxHeight: number = 0;

        if (isFitWidth) {
            setContainerWidth(width);

            maxHeight = isLandscape
                ? width / FIT_WIDTH_LANDSCAPE_MAX_ASPECT_RATIO
                : width / FIT_WIDTH_PORTRAIT_MAX_ASPECT_RATIO;
            setContainerHeight(height > maxHeight ? maxHeight : height);
            setFitMode(RwdFitModeEnum.FIT_WIDTH);
        } else {
            setContainerHeight(height);

            maxWidth = isLandscape
                ? height * FIT_HEIGHT_LANDSCAPE_MAX_ASPECT_RATIO
                : width; // height * FIT_HEIGHT_PORTRAIT_MAX_ASPECT_RATIO;
            setContainerWidth(width > maxWidth ? maxWidth : width);
            setFitMode(RwdFitModeEnum.FIT_HEIGHT);
        }
    }, [width, height, isLandscape]);

    useEffect(() => {
        document.documentElement.style.setProperty(
            '--global-scale-factor',
            `${scaleFactor}`
        );
    }, [scaleFactor]);

    useEffect(() => {
        document.documentElement.style.setProperty(
            '--game-container-width',
            `${containerWidth}px`
        );
        document.documentElement.style.setProperty(
            '--game-container-height',
            `${containerHeight}px`
        );
    }, [containerWidth, containerHeight]);

    useEffect(() => {
        const handleOrientationChange = (event: MediaQueryListEvent) => {
            setIsLandscape(event.matches);
        };

        const mediaQuery = window.matchMedia('(min-aspect-ratio: 4/3)');
        setIsLandscape(mediaQuery.matches);

        mediaQuery.addEventListener('change', handleOrientationChange);

        return () => {
            mediaQuery.removeEventListener('change', handleOrientationChange);
        };
    }, []);

    const state: ScreenState = {
        isLandscape,
        isMobile,
        scaleFactor,
        containerWidth,
        containerHeight,
        fitMode,
        isApple,
    };

    return <Provider value={state}>{children}</Provider>;
};
